import { css } from '@emotion/react';
import {
  ActionIcon,
  Box,
  Button,
  COLOR_PALETTE,
  Group,
  Icon,
  Text,
  desktopFirstMediaQuery,
  useTheme,
} from '@inflearn/ds-react-next';
import { faAngleDown, faAngleRight, faAngleUp } from '@inflearn/pro-regular-svg-icons';
import { isEmpty, isNil, isString, isUndefined } from 'lodash-es';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useState } from 'react';

import {
  type ClientApiV1BlogsUserUserIdQueryString,
  fetchClientApiV1BlogsUserUserId,
  fetchClientApiV1BlogsUserUserIdCategory,
  fetchClientApiV1BlogsUserUserIdTag,
} from '../../../../resources/@types/uccSchema';
import { logger } from '../../../../resources/Logger';
import EmptyStates from '../../../components/common/EmptyStates';
import CustomHead from '../../../components/layout/CustomHead';
import Layout from '../../../components/layout/Layout';
import BlogItem from '../../../components/pages/profile/BlogItem/BlogItem';
import BlogSkeletonItem from '../../../components/pages/profile/BlogItem/BlogSkeletonItem';
import GridList from '../../../components/pages/profile/GridList/GridList';
import Pagination from '../../../components/pages/profile/Pagination/Pagination';
import SectionHeader from '../../../components/pages/profile/SectionHeader/SectionHeader';
import { DeferredComponent } from '../../../components/util/DeferredComponent';
import { INFLEARN_URL } from '../../../constants/urls';
import useCheckBlackUser from '../../../hooks/useCheckBlackUser';
import useCheckMyProfile from '../../../hooks/useCheckMyProfile';
import { useCustomQueryParams } from '../../../hooks/useCustomQueryParams';
import useUserProfileQuery from '../../../hooks/useUserProfileQuery';
import {
  blogsUserUserIdCategoryOptions,
  useBlogsUserUserIdCategoryQuery,
} from '../../../queries/users/useBlogsUserUserIdCategoryQuery';
import {
  DEFAULT_QUERY_PARAMS,
  blogsUserUserIdOptions,
  useBlogsUserUserIdQuery,
} from '../../../queries/users/useBlogsUserUserIdQuery';
import {
  blogsUserUserIdTagOptions,
  useBlogsUserUserIdTagQuery,
} from '../../../queries/users/useBlogsUserUserIdTagQuery';
import { BLOG_FILTER } from '../../../utils/blog/blogType';
import { withProfileUserProps } from '../../../utils/page/withProfileUserProps';
import { withRemovedPageFallback } from '../../../utils/page/withRemovedPageFallback';
import { parseServerParams, schemas } from '../../../utils/server/parseServerParams';

function UserBlogsPage() {
  const { query } = useRouter();
  const { data: profile } = useUserProfileQuery({ id: Number(query.id) });
  const { t } = useTranslation();
  const { isMe } = useCheckMyProfile();
  const { isBlackUser } = useCheckBlackUser();

  const [isOpenFilter, setIsOpenFilter] = useState(true);

  const { queryParams, push } = useCustomQueryParams<ClientApiV1BlogsUserUserIdQueryString>({
    queryParamKeys: ['filter', 'filterName', 'pageNumber', 'pageSize'],
    defaultValue: DEFAULT_QUERY_PARAMS,
  });

  const pageNumber = Number(queryParams.pageNumber);
  const filter = isFilterQueryString(queryParams.filter) ? queryParams.filter : undefined;
  const filterName = isFilterNameQueryString(queryParams.filterName)
    ? queryParams.filterName
    : undefined;
  const hasNotFilter = isNil(filter) && isNil(filterName);

  const { data: categories } = useBlogsUserUserIdCategoryQuery({
    userId: profile?.id ?? 0,
    options: {
      enabled: Boolean(profile?.id),
    },
  });
  const { data: tags } = useBlogsUserUserIdTagQuery({
    userId: profile?.id ?? 0,
    options: {
      enabled: Boolean(profile?.id),
    },
  });
  const {
    data: blogs,
    isError,
    isLoading,
    refetch,
  } = useBlogsUserUserIdQuery({
    userId: profile?.id ?? 0,
    queryParams,
    options: {
      enabled: Boolean(profile?.id),
    },
  });

  const blogsTotalPage = blogs?.totalPage ?? 1;
  const blogsTotalCount = blogs?.totalCount;
  const hasFilterItems = !isEmpty(categories?.names) || !isEmpty(tags?.names);

  const onChangeFilter = (type?: BLOG_FILTER, value?: string) => {
    push({
      pageNumber: 1,
      filter: type,
      filterName: value,
    });
  };

  const onClearFilter = () => {
    push({
      pageNumber: 1,
      filter: undefined,
      filterName: undefined,
    });
  };

  const onToggleFilter = () => {
    setIsOpenFilter((prev) => !prev);
  };

  const userName = profile?.name;

  return (
    <Layout isInstructor={Boolean(profile?.isInstructor)} isInactive={Boolean(profile?.isInactive)}>
      <CustomHead
        robots={!isBlackUser}
        title={t('{{userName}}님의 블로그', { userName: userName })}
        description={t(
          '인프런 {{userName}}님의 블로그 페이지 입니다. - {{userName}}님 소개 | 인프런',
          { userName: userName },
        )}
        image={profile?.thumbnailUrl}
      />
      <SectionHeader styles={css({ marginBottom: '30px !important' })}>
        <SectionHeader.Title title={t('블로그')}>
          {hasNotFilter ? (
            !isNil(blogsTotalCount) && blogsTotalCount > 0 ? (
              <SectionHeader.TotalCount totalCount={blogsTotalCount} />
            ) : null
          ) : (
            <Text
              ml={8}
              mr={6}
              fw={500}
              size="sm"
              c="gray.7"
              onClick={onClearFilter}
              styles={{
                root: {
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  '&:hover': { textDecoration: 'underline' },
                },
              }}
            >
              {t('전체', { context: 'TOTAL' })}
            </Text>
          )}
          {hasNotFilter ? null : (
            <>
              <Icon icon={faAngleRight} size="xs" color="gray.5" />
              <Text
                ml={6}
                fw={700}
                size="sm"
                c="infgreen.6"
                styles={{
                  root: {
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                }}
              >
                {filterName}
              </Text>
              <Text ml={6} fw={700} size="sm" c="infgreen.6">
                {blogsTotalCount}
              </Text>
            </>
          )}
          {hasFilterItems ? (
            <ActionIcon
              ml={6}
              size="sm"
              radius="md"
              variant="light"
              color="infgreen"
              onClick={onToggleFilter}
              styles={{
                root: {
                  display: 'none',
                  [desktopFirstMediaQuery.tablet]: { display: 'inline-flex' },
                },
              }}
            >
              <Icon icon={isOpenFilter ? faAngleUp : faAngleDown} size="xs" />
            </ActionIcon>
          ) : null}
        </SectionHeader.Title>
        {isMe && blogsTotalCount !== 0 && !isBlackUser ? (
          <Button
            component="a"
            variant="default"
            size="sm"
            radius="md"
            href={INFLEARN_URL.NEW_BLOG()}
          >
            {t('작성하기')}
          </Button>
        ) : null}
      </SectionHeader>
      {isOpenFilter && hasFilterItems ? (
        <Box
          css={{
            display: 'none',
            [desktopFirstMediaQuery.tablet]: { display: 'block' },
          }}
        >
          <BlogFilter
            categories={categories?.names}
            tags={tags?.names}
            filter={filter}
            filterName={filterName}
            onChangeFilter={onChangeFilter}
          />
        </Box>
      ) : null}
      <EmptyStates
        height={300}
        isError={isError}
        isEmpty={!isLoading && isEmpty(blogs?.items)}
        onRetry={refetch}
        empty={{
          title: t('작성한 블로그 글이 없어요', { context: 'public' }),
          body: isMe ? t('첫번째 블로그 글을 작성해보세요.') : '',
          cta: isMe ? (
            <Button
              component="a"
              variant="default"
              size="sm"
              radius="md"
              href={INFLEARN_URL.NEW_BLOG()}
            >
              {t('작성하기')}
            </Button>
          ) : null,
        }}
      >
        <GridList mobileViewColumns={1} mobileGap={isLoading ? '20px 0' : 0}>
          {isLoading ? (
            <DeferredComponent>
              <BlogSkeletonItem count={6} />
            </DeferredComponent>
          ) : (
            blogs?.items?.map((blog) => (
              <Box
                key={blog.id}
                component="li"
                css={{
                  [desktopFirstMediaQuery.mobile]: {
                    padding: '20px 0',
                    borderBottom: '1px solid #E9ECEF',
                    '&:last-child': {
                      borderBottom: 'none',
                    },
                  },
                }}
              >
                <BlogItem {...blog} />
              </Box>
            ))
          )}
        </GridList>
        {blogsTotalPage === DEFAULT_QUERY_PARAMS.pageNumber ? null : (
          <Pagination
            value={pageNumber}
            total={blogsTotalPage}
            onChange={(page) => {
              push({ pageNumber: page }, { scroll: true });
            }}
          />
        )}
      </EmptyStates>
    </Layout>
  );
}

function BlogFilter({
  tags = [],
  categories = [],
  filter,
  filterName,
  onChangeFilter,
}: {
  tags?: string[];
  categories?: string[];
  filter?: ClientApiV1BlogsUserUserIdQueryString['filter'];
  filterName?: ClientApiV1BlogsUserUserIdQueryString['filterName'];
  onChangeFilter: (type?: BLOG_FILTER, value?: string) => void;
}) {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Box
      pt={12}
      pl={12}
      pb={20}
      css={{
        borderTop: `1px solid ${theme.colors.gray[2]}`,
        [desktopFirstMediaQuery.tablet]: {
          padding: '0 0 16px',
          marginBottom: 20,
          borderTop: 'none',
          borderBottom: `1px solid ${theme.colors.gray[2]}`,
          div: {
            display: 'flex',
            '&:first-of-type': {
              marginBottom: 8,
            },
            '& > p': {
              padding: 0,
              whiteSpace: 'nowrap',
            },
            ul: {
              marginLeft: 12,
              marginBottom: 0,
              flexWrap: 'nowrap',
              overflowX: 'auto',
              overflowY: 'hidden',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              li: {
                whiteSpace: 'nowrap',
              },
            },
          },
        },
        [desktopFirstMediaQuery.mobile]: {
          marginBottom: 0,
        },
      }}
    >
      {isEmpty(categories) ? null : (
        <Box>
          <Text size="sm" fw={700} py={8}>
            {t('#카테고리')}
          </Text>
          <Group
            component="ul"
            styles={{
              root: { gap: '8px 16px' },
            }}
            mb={8}
          >
            {categories?.map((category) => {
              const isActive = filter === 'CATEGORY' && filterName === category;

              return (
                <Text
                  component="li"
                  size="sm"
                  c="gray.5"
                  key={category}
                  styles={{
                    root: styleBlogFilterText(isActive),
                  }}
                  onClick={() =>
                    isActive
                      ? onChangeFilter(undefined, undefined)
                      : onChangeFilter(BLOG_FILTER.CATEGORY, category)
                  }
                >
                  {category}
                </Text>
              );
            })}
          </Group>
        </Box>
      )}
      {isEmpty(tags) ? null : (
        <Box>
          <Text size="sm" fw={700} py={8}>
            {t('#태그')}
          </Text>
          <Group
            component="ul"
            styles={{
              root: { gap: '8px 16px' },
            }}
          >
            {tags?.map((tag) => {
              const isActive = filter === 'TAG' && filterName === tag;

              return (
                <Text
                  component="li"
                  size="sm"
                  c="gray.5"
                  key={tag}
                  styles={{
                    root: styleBlogFilterText(isActive),
                  }}
                  onClick={() =>
                    isActive
                      ? onChangeFilter(undefined, undefined)
                      : onChangeFilter(BLOG_FILTER.TAG, tag)
                  }
                >
                  {tag}
                </Text>
              );
            })}
          </Group>
        </Box>
      )}
    </Box>
  );
}

const isFilterQueryString = (
  filter: unknown,
): filter is ClientApiV1BlogsUserUserIdQueryString['filter'] =>
  ['CATEGORY', 'TAG'].includes(String(filter)) || isUndefined(filter);

const isFilterNameQueryString = (
  filterName: unknown,
): filterName is ClientApiV1BlogsUserUserIdQueryString['filterName'] =>
  isUndefined(filterName) || isString(filterName);

const styleBlogFilterText = (isActive: boolean) => ({
  cursor: 'pointer',
  fontWeight: isActive ? 700 : '',
  color: isActive ? COLOR_PALETTE.infgreen[6] : '',

  '&:hover': {
    color: isActive ? '' : COLOR_PALETTE.gray[7],
    textDecoration: isActive ? '' : 'underline',
  },
});

export const getServerSideProps = withProfileUserProps(async ({ context, queryClient }) => {
  try {
    const params = parseServerParams(context.params, {
      id: schemas.string(),
      filter: schemas.enum(Object.values(BLOG_FILTER)),
      filterName: schemas.stringOrUndefined(),
      pageNumber: schemas.number().default(DEFAULT_QUERY_PARAMS.pageNumber),
    });

    const userId = params.id;

    if (!userId) {
      throw new Error(`유저 정보를 가져올 수 없습니다. *userId: ${userId}`);
    }

    const numericUserId = Number(userId);
    const queryParams = {
      ...DEFAULT_QUERY_PARAMS,
      ...(params.pageNumber && { pageNumber: params.pageNumber }),
      ...(params.filter && { filter: params.filter }),
      ...(params.filterName && { filterName: params.filterName }),
    };

    const blogsOptions = blogsUserUserIdOptions(numericUserId, queryParams);
    const categoriesOptions = blogsUserUserIdCategoryOptions(numericUserId);
    const tagsOptions = blogsUserUserIdTagOptions(numericUserId);

    const results = await Promise.allSettled([
      queryClient.fetchQuery({
        queryKey: blogsOptions.queryKey,
        queryFn: () => fetchClientApiV1BlogsUserUserId(blogsOptions.url),
      }),
      queryClient.fetchQuery({
        queryKey: categoriesOptions.queryKey,
        queryFn: () => fetchClientApiV1BlogsUserUserIdCategory(categoriesOptions.url),
      }),
      queryClient.fetchQuery({
        queryKey: tagsOptions.queryKey,
        queryFn: () => fetchClientApiV1BlogsUserUserIdTag(tagsOptions.url),
      }),
    ]);

    const queryTypes = ['blogs', 'categories', 'tags'];
    results.forEach((result, index) => {
      if (result.status === 'rejected') {
        const queryType = queryTypes[index];
        throw new Error(`Query failed for ${queryType}: ${result.reason}`);
      }
    });

    return {
      props: {},
    };
  } catch (error) {
    logger.info({
      type: '[SSR_ERROR] 유저프로필 블로그 페이지',
      location: `/users/${context.params?.id}/blogs`,
      error: error instanceof Error ? error.message : error,
      params: context.params,
      query: context.query,
    });

    return {
      props: {},
    };
  }
});

export default withRemovedPageFallback(UserBlogsPage);
